import React, { useState, useEffect, useCallback } from 'react';
import { professorService, Professor, professorAgendaService, Agenda, agendaProfessorService, AgendaProfessor, disciplinaService, ProfessorDisciplina, laboratorioDisciplinaService, LaboratorioDisciplina, laboratorioService, Laboratorio } from '../services/api';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import LaboratorioDisciplinaModal from './LaboratorioDisciplinaModal';

const Professores: React.FC = () => {
  const [professores, setProfessores] = useState<Professor[]>([]);
  const [filteredProfessores, setFilteredProfessores] = useState<Professor[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingProfessor, setEditingProfessor] = useState<Professor | null>(null);
  const [selectedProfessorId, setSelectedProfessorId] = useState<number | null>(null);
  const [selectedAgendaId, setSelectedAgendaId] = useState<string | null>(null);
  const [agendas, setAgendas] = useState<Agenda[]>([]);
  const [agendaHorarios, setAgendaHorarios] = useState<AgendaProfessor[]>([]);
  const [loadingAgendas, setLoadingAgendas] = useState(false);
  const [loadingHorarios, setLoadingHorarios] = useState(false);
  const [professorDisciplinas, setProfessorDisciplinas] = useState<ProfessorDisciplina | null>(null);
  const [loadingDisciplinas, setLoadingDisciplinas] = useState(false);
  const [totalHorariosLivres, setTotalHorariosLivres] = useState(0);
  const [loadingHorariosLivres, setLoadingHorariosLivres] = useState(false);
  const [laboratorioDisciplinas, setLaboratorioDisciplinas] = useState<{[key: string]: LaboratorioDisciplina | null}>({});
  const [laboratorios, setLaboratorios] = useState<Laboratorio[]>([]);
  const [showLaboratorioModal, setShowLaboratorioModal] = useState(false);
  const [disciplinaParaLaboratorio, setDisciplinaParaLaboratorio] = useState<{ id: string; nome: string; cargaHoraria: number } | null>(null);
  const [formData, setFormData] = useState<Professor>({
    nome: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    itemName: '',
    onConfirm: () => {},
    isLoading: false
  });

  useEffect(() => {
    loadProfessores();
    loadLaboratorios(); // Carregar laboratórios no início
  }, []);

  const calcularHorariosLivres = async (professorId: number) => {
    try {
      setLoadingHorariosLivres(true);
      const agendasProfessor = await professorAgendaService.getAgendasByProfessor(professorId);
      
      let totalLivres = 0;
      
      // Para cada agenda do professor, buscar os horários e contar os disponíveis
      for (const agenda of agendasProfessor) {
        if (agenda.id) {
          try {
            const horariosAgenda = await agendaProfessorService.getByAgenda(agenda.id.toString());
            const horariosDisponiveis = horariosAgenda.filter(h => h.disponivel);
            totalLivres += horariosDisponiveis.length;
          } catch (error) {
            console.error(`Erro ao carregar horários da agenda ${agenda.id}:`, error);
          }
        }
      }
      
      setTotalHorariosLivres(totalLivres);
    } catch (error) {
      console.error('Erro ao calcular horários livres:', error);
      setTotalHorariosLivres(0);
    } finally {
      setLoadingHorariosLivres(false);
    }
  };

  const loadProfessores = async () => {
    try {
      setLoading(true);
      const data = await professorService.getAll();
      setProfessores(data);
      setFilteredProfessores(data);
    } catch (error) {
      console.error('Erro ao carregar professores:', error);
      setError('Erro ao carregar professores');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectProfessor = async (professorId: number) => {
    try {
      // Fechar formulário de novo professor ao selecionar um professor
      setShowForm(false);
      setSelectedProfessorId(professorId);
      setLoadingAgendas(true);
      setLoadingDisciplinas(true);
      setError('');
      
      // Carregar agendas, disciplinas e calcular horários livres em paralelo
      const [agendasData, disciplinasData] = await Promise.all([
        professorAgendaService.getAgendasByProfessor(professorId),
        disciplinaService.getByProfessor(professorId)
      ]);
      
      setAgendas(agendasData);
      setProfessorDisciplinas(disciplinasData);
      
      // Carregar laboratórios vinculados às disciplinas
      if (disciplinasData && disciplinasData.disciplinas.length > 0) {
        await loadLaboratoriosDisciplinas(disciplinasData.disciplinas.map(d => d.id));
      }
      
      // Calcular horários livres após carregar as agendas
      calcularHorariosLivres(professorId);
    } catch (error) {
      console.error('Erro ao carregar dados do professor:', error);
      setError('Erro ao carregar dados do professor');
      setAgendas([]);
      setProfessorDisciplinas(null);
      setTotalHorariosLivres(0);
    } finally {
      setLoadingAgendas(false);
      setLoadingDisciplinas(false);
    }
  };

  const handleSelectAgenda = async (agendaId: string) => {
    try {
      // Fechar formulário de novo professor ao acessar gerenciar agenda
      setShowForm(false);
      setSelectedAgendaId(agendaId);
      setLoadingHorarios(true);
      setError('');
      
      const horariosData = await agendaProfessorService.getByAgenda(agendaId);
      setAgendaHorarios(horariosData);
    } catch (error) {
      console.error('Erro ao carregar horários da agenda:', error);
      setError('Erro ao carregar horários da agenda');
      setAgendaHorarios([]);
    } finally {
      setLoadingHorarios(false);
    }
  };

  const handleBackToProfessores = () => {
    setSelectedProfessorId(null);
    setSelectedAgendaId(null);
    setAgendas([]);
    setAgendaHorarios([]);
    setProfessorDisciplinas(null);
    setTotalHorariosLivres(0);
  };

  const handleBackToAgendas = () => {
    setSelectedAgendaId(null);
    setAgendaHorarios([]);
  };

  const handleToggleDisponibilidade = async (agendaProfessorId: string | number, currentDisponivel: boolean) => {
    try {
      await agendaProfessorService.updateDisponibilidade(agendaProfessorId.toString(), { disponivel: !currentDisponivel });
      
      setAgendaHorarios(prev => 
        prev.map(item => 
          item.id === agendaProfessorId 
            ? { ...item, disponivel: !currentDisponivel }
            : item
        )
      );
      
      // Removida a mensagem de confirmação para melhor usabilidade
    } catch (error) {
      console.error('Erro ao atualizar disponibilidade:', error);
      setError('Erro ao atualizar disponibilidade');
    }
  };

  const handleToggleDayAvailability = async (diaSemana: number) => {
    try {
      const horariosDay = agendaHorarios.filter(h => h.horario.diaSemana === diaSemana);
      
      if (horariosDay.length === 0) return;
      
      // Determinar se deve ativar ou desativar todos os horários do dia
      // Se todos estão disponíveis, desativa todos. Caso contrário, ativa todos.
      const todosDisponiveis = horariosDay.every(h => h.disponivel);
      const novoStatus = !todosDisponiveis;
      
      // Atualizar todos os horários do dia
      const promises = horariosDay.map(horario => 
        agendaProfessorService.updateDisponibilidade(horario.id!.toString(), { disponivel: novoStatus })
      );
      
      await Promise.all(promises);
      
      // Atualizar o estado local
      setAgendaHorarios(prev => 
        prev.map(item => 
          horariosDay.some(h => h.id === item.id)
            ? { ...item, disponivel: novoStatus }
            : item
        )
      );
      
    } catch (error) {
      console.error('Erro ao atualizar disponibilidade do dia:', error);
      setError('Erro ao atualizar disponibilidade do dia');
    }
  };

  const formatarHorario = (inicio: string, fim: string) => {
    return `${inicio} - ${fim}`;
  };

  // Funções para gerenciar laboratórios das disciplinas
  const loadLaboratoriosDisciplinas = async (disciplinaIds: string[]) => {
    try {
      const laboratoriosMap: {[key: string]: LaboratorioDisciplina | null} = {};
      
      for (const disciplinaId of disciplinaIds) {
        const laboratorio = await laboratorioDisciplinaService.getByDisciplina(disciplinaId);
        laboratoriosMap[disciplinaId] = laboratorio;
      }
      
      setLaboratorioDisciplinas(laboratoriosMap);
    } catch (error) {
      console.error('Erro ao carregar laboratórios das disciplinas:', error);
    }
  };

  const loadLaboratorios = async () => {
    try {
      const data = await laboratorioService.getAll();
      setLaboratorios(data);
    } catch (error) {
      console.error('Erro ao carregar laboratórios:', error);
    }
  };

  const handleAbrirLaboratorioModal = (disciplinaId: string, nome: string, cargaHoraria: number) => {
    setDisciplinaParaLaboratorio({ id: disciplinaId, nome, cargaHoraria });
    setShowLaboratorioModal(true);
  };

  const handleLaboratorioSucesso = () => {
    if (professorDisciplinas && professorDisciplinas.disciplinas.length > 0) {
      loadLaboratoriosDisciplinas(professorDisciplinas.disciplinas.map(d => d.id));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.nome.trim()) {
      setError('Nome é obrigatório');
      return;
    }

    try {
      setSaving(true);
      setError('');
      
      if (editingProfessor) {
        await professorService.update(editingProfessor.id!, formData);
        setSuccess('Professor atualizado com sucesso!');
      } else {
        await professorService.create(formData);
        setSuccess('Professor cadastrado com sucesso!');
      }
      
      await loadProfessores();
      resetForm();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Erro ao salvar professor:', error);
      setError('Erro ao salvar professor');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({ nome: '' });
    setEditingProfessor(null);
    setShowForm(false);
    setError('');
  };

  const handleEdit = (professor: Professor) => {
    setFormData(professor);
    setEditingProfessor(professor);
    setShowForm(true);
  };

  const handleDelete = (professorId: number) => {
    const professor = professores.find(p => p.id === professorId);
    if (!professor) return;

    setDeleteModal({
      isOpen: true,
      title: 'Confirmar Exclusão',
      message: 'Tem certeza que deseja excluir este professor? Esta ação não pode ser desfeita.',
      itemName: professor.nome,
      onConfirm: () => confirmDelete(professorId),
      isLoading: false
    });
  };

  const confirmDelete = async (professorId: number) => {
    try {
      setDeleteModal(prev => ({ ...prev, isLoading: true }));
      await professorService.delete(professorId);
      await loadProfessores();
      setDeleteModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
      
      // Retornar automaticamente para a listagem de professores após exclusão
      setSelectedProfessorId(null);
      setSelectedAgendaId(null);
      setAgendas([]);
      setAgendaHorarios([]);
      setProfessorDisciplinas(null);
      
      setSuccess('Professor excluído com sucesso!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Erro ao excluir professor:', error);
      setError('Erro ao excluir professor');
      setDeleteModal(prev => ({ ...prev, isLoading: false }));
    }
  };

  const handleDeleteDisciplina = (disciplinaId: string) => {
    const disciplina = professorDisciplinas?.disciplinas.find(d => d.id === disciplinaId);
    if (!disciplina) return;

    setDeleteModal({
      isOpen: true,
      title: 'Confirmar Exclusão',
      message: 'Tem certeza que deseja excluir esta disciplina? Esta ação não pode ser desfeita.',
      itemName: disciplina.nome,
      onConfirm: () => confirmDeleteDisciplina(disciplinaId),
      isLoading: false
    });
  };

  const confirmDeleteDisciplina = async (disciplinaId: string) => {
    try {
      setDeleteModal(prev => ({ ...prev, isLoading: true }));
      await disciplinaService.delete(disciplinaId);
      
      // Recarregar disciplinas do professor
      if (selectedProfessorId) {
        const disciplinasData = await disciplinaService.getByProfessor(selectedProfessorId);
        setProfessorDisciplinas(disciplinasData);
      }
      
      setDeleteModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
      setSuccess('Disciplina excluída com sucesso!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Erro ao excluir disciplina:', error);
      setError('Erro ao excluir disciplina');
      setDeleteModal(prev => ({ ...prev, isLoading: false }));
    }
  };

  const handleSearch = useCallback((term: string) => {
    setSearchTerm(term);
    if (term.trim() === '') {
      setFilteredProfessores(professores);
    } else {
      const filtered = professores.filter(professor =>
        professor.nome.toLowerCase().includes(term.toLowerCase())
      );
      setFilteredProfessores(filtered);
    }
  }, [professores]);

  useEffect(() => {
    handleSearch(searchTerm);
  }, [searchTerm, handleSearch]);

  return (
    <div className="main-content">
      <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-start mb-4">
              <div className="flex-grow-1">
                <h2 className="mb-2 text-start">
                  <i className="fas fa-chalkboard-teacher me-2"></i>
                  Professores
                </h2>
              </div>
            {!selectedProfessorId && !selectedAgendaId ? (
              <button
                className="btn btn-success"
                onClick={() => setShowForm(!showForm)}
              >
                <i className="fas fa-plus me-2"></i>
                {showForm ? 'Cancelar' : 'Novo Professor'}
              </button>
            ) : selectedAgendaId ? (
              <button className="btn btn-outline-secondary" onClick={handleBackToAgendas}>
                <i className="fas fa-arrow-left me-2"></i>
                Voltar para Agendas
              </button>
            ) : (
              <button className="btn btn-outline-secondary" onClick={handleBackToProfessores}>
                <i className="fas fa-arrow-left me-2"></i>
                Voltar para Professores
              </button>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
          <button type="button" className="btn-close" onClick={() => setError('')}></button>
        </div>
      )}

      {success && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          <i className="fas fa-check-circle me-2"></i>
          {success}
          <button type="button" className="btn-close" onClick={() => setSuccess('')}></button>
        </div>
      )}

      {showForm && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="card">
              <div className="card-header">
                <h5 className="mb-0">
                  <i className="fas fa-user-plus me-2"></i>
                  {editingProfessor ? 'Editar Professor' : 'Cadastrar Novo Professor'}
                </h5>
              </div>
              <div className="card-body p-4">
                <form onSubmit={handleSubmit}>
                  <div className="row mb-4">
                    <div className="col-md-6 mb-3">
                      <label htmlFor="nome" className="form-label fw-semibold">Nome do Professor *</label>
                      <input
                        type="text"
                        className="form-control form-control-lg"
                        id="nome"
                        name="nome"
                        value={formData.nome}
                        onChange={handleInputChange}
                        placeholder="Ex: João Silva, Maria Santos..."
                        maxLength={30}
                        style={{
                          borderRadius: '8px',
                          border: '2px solid #e9ecef',
                          transition: 'all 0.2s ease'
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = '#007bff';
                          e.target.style.boxShadow = '0 0 0 0.2rem rgba(0, 123, 255, 0.25)';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#e9ecef';
                          e.target.style.boxShadow = 'none';
                        }}
                        required
                      />
                      <div className="d-flex gap-3 justify-content-end mt-3">
                        <button
                          type="button"
                          className="btn btn-outline-secondary px-4 py-2"
                          onClick={resetForm}
                          disabled={saving}
                          style={{
                            borderRadius: '8px',
                            fontWeight: '500',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          <i className="fas fa-times me-2"></i>
                          Cancelar
                        </button>
                        <button
                          type="submit"
                          className="btn btn-success px-4 py-2"
                          disabled={saving}
                          style={{
                            borderRadius: '8px',
                            fontWeight: '500',
                            minWidth: '120px'
                          }}
                        >
                          {saving ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              {editingProfessor ? 'Atualizando...' : 'Cadastrando...'}
                            </>
                          ) : (
                            <>
                              <i className="fas fa-save me-2"></i>
                              {editingProfessor ? 'Atualizar' : 'Cadastrar'}
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Conteúdo Principal */}
      {selectedAgendaId ? (
        /* Visualização de Horários da Agenda */
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-header">
                <h5 className="mb-0">
                  <i className="fas fa-clock me-2"></i>
                  Gerenciar Horários da Agenda
                </h5>
              </div>
              <div className="card-body">
                
                <div className="alert alert-info mb-4">
                  <div className="row">
                    <div className="col-md-6">
                      <strong>
                        <i className="fas fa-user me-2"></i>
                        Professor:
                      </strong>
                      <span className="ms-2">
                        {professores.find(p => p.id === selectedProfessorId)?.nome || 'Não identificado'}
                      </span>
                    </div>
                    <div className="col-md-6">
                      <strong>
                        <i className="fas fa-calendar me-2"></i>
                        Período:
                      </strong>
                      <span className="ms-2">
                        {agendas.find(a => a.id?.toString() === selectedAgendaId)?.periodo?.nome || 'Não identificado'}
                      </span>
                    </div>
                  </div>
                </div>

                {loadingHorarios ? (
                  <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Carregando horários...</span>
                    </div>
                  </div>
                ) : (
                  <div>
                    {agendaHorarios.length === 0 ? (
                      <div className="text-center py-5">
                        <i className="fas fa-clock fa-3x text-muted mb-3"></i>
                        <h5 className="text-muted">Nenhum horário encontrado</h5>
                        <p className="text-muted">Esta agenda não possui horários cadastrados.</p>
                      </div>
                    ) : (
                      <div className="row">
                        {/* Colunas dos dias da semana */}
                        {[1, 2, 3, 4, 5, 6, 0].map(diaSemana => {
                          const diasNomes = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'];
                          const horariosDay = agendaHorarios.filter(h => h.horario.diaSemana === diaSemana);
                          
                          if (horariosDay.length === 0) return null;
                          
                          return (
                            <div key={diaSemana} className="col-lg-2 col-md-4 col-sm-6 mb-4">
                              <div className="card" style={{ minHeight: '400px' }}>
                                <div 
                                  className="card-header bg-primary text-white text-center" 
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => handleToggleDayAvailability(diaSemana)}
                                  title="Clique para inverter a disponibilidade de todos os horários deste dia"
                                >
                                  <h6 className="mb-0">{diasNomes[diaSemana]}</h6>
                                  <small style={{ fontSize: '0.7em', opacity: 0.8 }}>Clique para inverter</small>
                                </div>
                                <div className="card-body p-2">
                                  {horariosDay
                                    .sort((a, b) => a.horario.inicio.localeCompare(b.horario.inicio))
                                    .map((agendaHorario) => (
                                      <div 
                                        key={agendaHorario.id} 
                                        className={`card mb-2 ${agendaHorario.disponivel ? 'border-success' : 'border-danger'}`}
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => handleToggleDisponibilidade(agendaHorario.id!, agendaHorario.disponivel)}
                                      >
                                        <div className="card-body p-2 text-center">
                                          <div className={`mb-1 ${agendaHorario.disponivel ? 'text-success' : 'text-danger'}`}>
                                            <i className={`fas ${agendaHorario.disponivel ? 'fa-check-circle' : 'fa-times-circle'}`}></i>
                                          </div>
                                          <small className="d-block">
                                            <strong>{formatarHorario(agendaHorario.horario.inicio, agendaHorario.horario.fim)}</strong>
                                          </small>
                                          <span className={`badge ${agendaHorario.disponivel ? 'bg-success' : 'bg-danger'} mt-1`} style={{ fontSize: '0.7em' }}>
                                            {agendaHorario.disponivel ? 'Disponível' : 'Indisponível'}
                                          </span>
                                        </div>
                                      </div>
                                    ))}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : selectedProfessorId ? (
        /* Visualização de Agendas do Professor */
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="mb-0">
                    <i className="fas fa-user me-2"></i>
                    Professor: {professores.find(p => p.id === selectedProfessorId)?.nome || 'Não identificado'}
                  </h5>
                  <small className="text-muted">Agendas do Professor</small>
                </div>
                <div className="d-flex gap-2">
                  <button
                      className="btn btn-success"
                      style={{ backgroundColor: 'rgb(13, 110, 253)', borderColor: 'rgb(13, 110, 253)' }}
                      onClick={() => {
                        const professor = professores.find(p => p.id === selectedProfessorId);
                        if (professor) handleEdit(professor);
                      }}
                    >
                    <i className="fas fa-edit me-2"></i>
                    Editar Professor
                  </button>
                  <button
                    className="btn btn-danger"
                    onClick={() => {
                      if (selectedProfessorId) handleDelete(selectedProfessorId);
                    }}
                  >
                    <i className="fas fa-trash me-2"></i>
                    Excluir Professor
                  </button>
                </div>
              </div>
              <div className="card-body">

                {loadingAgendas ? (
                  <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Carregando agendas...</span>
                    </div>
                  </div>
                ) : (
                  <div className="row">
                    {agendas.length === 0 ? (
                      <div className="col-12">
                        <div className="text-center py-5">
                          <i className="fas fa-calendar-times fa-3x text-muted mb-3"></i>
                          <h5 className="text-muted">Nenhuma agenda encontrada</h5>
                          <p className="text-muted">Este professor não possui agendas cadastradas.</p>
                        </div>
                      </div>
                    ) : (
                      agendas.map((agenda) => (
                        <div key={agenda.id} className="col-xl-4 col-lg-6 col-md-6 col-12 mb-4">
                          <div className="card h-100">
                            <div className="card-body">
                              <div className="d-flex align-items-center mb-3">
                                <div className="rounded-circle bg-primary d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px' }}>
                                  <i className="fas fa-calendar text-white"></i>
                                </div>
                                <div>
                                  <h6 className="card-title mb-0">{agenda.nome}</h6>
                                  <small className="text-muted">{agenda.periodo?.nome}</small>
                                </div>
                              </div>
                              <div className="d-grid">
                                <button 
                                  className="btn btn-outline-primary"
                                  onClick={() => handleSelectAgenda(agenda.id!.toString())}
                                >
                                  <i className="fas fa-cog me-2"></i>
                                  Gerenciar
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Disciplinas Cadastradas do Professor */}
          <div className="col-12 mt-4">
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  <i className="fas fa-book me-2"></i>
                  Disciplinas Cadastradas
                </h5>
                <div className="d-flex gap-3">
                  <div className="badge bg-primary d-flex align-items-center fs-6 px-3 py-2">
                    <i className="fas fa-list-ol me-2"></i>
                    Total: {professorDisciplinas ? professorDisciplinas.disciplinas.length : 0}
                  </div>
                  <div className="badge bg-success d-flex align-items-center fs-6 px-3 py-2">
                    <i className="fas fa-clock me-2"></i>
                    Carga Horária: {professorDisciplinas ? professorDisciplinas.disciplinas.reduce((total, d) => total + d.cargaHoraria, 0) : 0}h
                  </div>
                  <div className="badge bg-info d-flex align-items-center fs-6 px-3 py-2">
                    <i className="fas fa-calendar-check me-2"></i>
                    {loadingHorariosLivres ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" style={{ width: '12px', height: '12px' }}></span>
                        Calculando...
                      </>
                    ) : (
                      `Horários Livres: ${totalHorariosLivres}`
                    )}
                  </div>
                </div>
              </div>
              <div className="card-body">
                {loadingDisciplinas ? (
                  <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Carregando disciplinas...</span>
                    </div>
                  </div>
                ) : (
                  professorDisciplinas && professorDisciplinas.disciplinas.length > 0 ? (
                    <div 
                      className="table-responsive" 
                      style={{
                        maxHeight: '800px',
                        overflowY: 'auto',
                        border: '1px solid #dee2e6',
                        borderRadius: '0.375rem'
                      }}
                    >
                      <table className="table table-striped table-hover mb-0">
                        <thead className="table-dark sticky-top">
                          <tr>
                            <th>Nome da Disciplina</th>
                            <th>Carga Horária</th>
                            <th>Turma</th>
                            <th>Período</th>
                            <th>Laboratório</th>
                            <th>Ações</th>
                          </tr>
                        </thead>
                        <tbody>
                          {professorDisciplinas.disciplinas.map((disciplina) => (
                            <tr key={disciplina.id}>
                              <td>{disciplina.nome}</td>
                              <td>{disciplina.cargaHoraria}h</td>
                              <td>{disciplina.turma.nome}</td>
                              <td>{disciplina.turma.periodo.nome}</td>
                              <td>
                                <button
                                  className={`btn btn-sm ${laboratorioDisciplinas[disciplina.id] ? 'btn-outline-info' : 'btn-outline-secondary'}`}
                                  onClick={() => handleAbrirLaboratorioModal(disciplina.id, disciplina.nome, disciplina.cargaHoraria)}
                                  title="Configurar aulas em laboratório desta disciplina"
                                >
                                  <i className="fas fa-flask me-2"></i>
                                  {laboratorioDisciplinas[disciplina.id]
                                    ? `${laboratorioDisciplinas[disciplina.id]?.quantidade} aula(s) · ${laboratorios.find(lab => lab.id === laboratorioDisciplinas[disciplina.id]?.laboratorioId)?.nome || 'laboratório'}`
                                    : 'Sem laboratório'}
                                </button>
                              </td>
                              <td>
                                <div className="btn-group" role="group">
                                  <button
                                    className="btn btn-outline-danger btn-sm"
                                    onClick={() => handleDeleteDisciplina(disciplina.id)}
                                    title="Excluir disciplina permanentemente"
                                  >
                                    <i className="fas fa-trash-alt"></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-5">
                      <i className="fas fa-book-open fa-3x text-muted mb-3"></i>
                      <h5 className="text-muted">Nenhuma disciplina encontrada</h5>
                      <p className="text-muted">Este professor não possui disciplinas cadastradas.</p>
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Lista de Professores */
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-header">
                <div className="row align-items-center">
                  <div className="col-md-6">
                    <h5 className="mb-0 text-center">
                      <i className="fas fa-users me-2"></i>
                      Professores Cadastrados
                    </h5>
                  </div>
                  <div className="col-md-6">
                    <div className="input-group" style={{ width: '300px', marginLeft: 'auto' }}>
                      <span className="input-group-text">
                        <i className="fas fa-search"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Buscar professor..."
                        value={searchTerm}
                        onChange={(e) => handleSearch(e.target.value)}
                        maxLength={30}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="card-body">

                {loading ? (
                  <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Carregando professores...</span>
                    </div>
                  </div>
                ) : (
                  <div className="row">
                    {filteredProfessores.length === 0 ? (
                      <div className="col-12">
                        <div className="text-center py-5">
                          <i className="fas fa-user-times fa-3x text-muted mb-3"></i>
                          <h5 className="text-muted">Nenhum professor encontrado</h5>
                          <p className="text-muted">
                            {searchTerm ? 'Tente ajustar os termos de busca.' : 'Comece cadastrando um novo professor.'}
                          </p>
                        </div>
                      </div>
                    ) : (
                      filteredProfessores.map((professor) => (
                        <div key={professor.id} className="col-xl-3 col-lg-4 col-md-6 col-12 mb-3">
                          <div className="turma-card" style={{ cursor: 'pointer' }} onClick={() => handleSelectProfessor(professor.id!)}>
                            <div className="d-flex align-items-center justify-content-between">
                              <div className="d-flex align-items-center">
                                <div className="bg-primary rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '50px', height: '50px' }}>
                                  <i className="fas fa-user text-white"></i>
                                </div>
                                <div>
                                  <h6 className="mb-0">{professor.nome}</h6>
                                  <small className="text-muted">
                                    {selectedProfessorId === professor.id ? (
                                      <>
                                        <i className="fas fa-check me-1"></i>
                                        Selecionado
                                      </>
                                    ) : (
                                      'Clique para ver as agendas'
                                    )}
                                  </small>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação */}
      <DeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        title={deleteModal.title}
        message={deleteModal.message}
        itemName={deleteModal.itemName}
        onConfirm={deleteModal.onConfirm}
        onClose={() => setDeleteModal((prev: any) => ({ ...prev, isOpen: false }))}
        isLoading={deleteModal.isLoading}
      />

      {disciplinaParaLaboratorio && (
        <LaboratorioDisciplinaModal
          isOpen={showLaboratorioModal}
          onClose={() => setShowLaboratorioModal(false)}
          disciplina={disciplinaParaLaboratorio}
          laboratorios={laboratorios}
          onSucesso={handleLaboratorioSucesso}
        />
      )}
      </div>
    </div>
  );
};

export default Professores;